import { Link, useNavigate } from '@tanstack/react-router';
import { LayoutDashboard, Building2, LogOut, ShieldCheck } from 'lucide-react';
import { staffLogout } from '../../api/staff';
import { useStaffAuthStore } from '../../store/staffAuth';
import { useT } from '../../i18n';
import type { ReactNode } from 'react';

// ANX-087 — каркас staff-админки: тёмная top-bar, отдельная от операторской
// панели. Никакого operator-Sidebar.
export function StaffShell({ children }: { children: ReactNode }) {
  const t = useT();
  const session = useStaffAuthStore((s) => s.session);
  const navigate = useNavigate();

  async function logout() {
    await staffLogout();
    await navigate({ to: '/staff/login' });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-800 bg-slate-900 text-slate-100">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3">
          <Link to="/staff" className="flex items-center gap-2 font-semibold">
            <ShieldCheck className="h-5 w-5 text-accent-400" aria-hidden="true" />
            {t('staff.brand')}
          </Link>
          <nav className="flex items-center gap-1 text-sm">
            <StaffNavLink to="/staff" icon={LayoutDashboard} label={t('staff.nav.dashboard')} />
            <StaffNavLink to="/staff/tenants" icon={Building2} label={t('staff.nav.tenants')} />
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm text-slate-400">
            <span className="hidden sm:inline">{session?.staff.email}</span>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 hover:text-slate-100"
            >
              <LogOut size={14} aria-hidden="true" />
              {t('staff.nav.logout')}
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-6">{children}</main>
    </div>
  );
}

function StaffNavLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
}) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: to === '/staff' }}
      className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white"
      activeProps={{ className: 'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 bg-slate-800 text-white' }}
    >
      <Icon size={15} aria-hidden="true" />
      {label}
    </Link>
  );
}
